import { Meta } from '@/components/common';
import { ChangePasswordCard, SessionsCard, TeamSessionsCard } from '@/components/features/account';
import { ProfileCard, SeatCard, TeamRoster } from '@/components/features/profile';
import { Container } from '@/components/layout';
import { useLicence } from '@/services/queries';
import { useAppSelector } from '@/store/hooks';

/**
 * Profile, laid out as the design draws it.
 *
 * ── The two variants ────────────────────────────────────────────────────────
 *
 * The design draws this screen twice, switched on whether the account is a
 * single seat or a lab:
 *
 * - Single seat: profile card beside a narrow column carrying your seat.
 * - Lab: the team roster across the top, profile underneath.
 *
 * The switch is real rather than cosmetic. A one-person practice has no roster
 * to show and would get a table with one row in it; a lab of six wants the
 * roster first, because "who has which seat" is the reason they opened the
 * page. Driven off the licence mode, which is the thing that actually differs.
 *
 * ── Where Team went ────────────────────────────────────────────────────────
 *
 * There was a separate Team page. The design has no such page and puts the
 * roster here, which is right: a roster is a list of who holds a seat, and a
 * seat is a licensing fact. Splitting them meant two pages reading the same
 * rows.
 *
 * ── Account ────────────────────────────────────────────────────────────────
 *
 * Under either variant: Change password (where the emails that hand somebody
 * a password send them) beside "Where you're signed in". A lab owner also gets
 * Team sessions - where everyone else on the licence is signed in, with a way
 * to sign them out.
 */
export function ProfilePage() {
  const query = useLicence();
  const licence = query.data?.licence ?? null;

  // Until the licence loads, assume the narrower layout. Guessing "lab" would
  // render a roster skeleton the width of the page and then collapse it.
  const isLab = licence !== null && licence.mode !== 'single_seat';
  // The team roster is the owner's alone; a technician or viewer on a lab
  // licence gets the single-seat layout - their profile and their own seat.
  const isOwner = useAppSelector((state) => state.auth.user?.role === 'owner');

  return (
    <>
      <Meta title="Profile" />

      {isLab && isOwner ? (
        <Container className="space-y-3.5">
          <TeamRoster />
          <ProfileCard />
          <AccountCards />
          <TeamSessionsCard />
        </Container>
      ) : (
        <Container className="space-y-3.5">
          <div className="flex flex-col items-start gap-3.5 lg:flex-row">
            <div className="min-w-0 w-full lg:flex-[2_1_420px]">
              <ProfileCard />
            </div>
            <div className="min-w-0 w-full lg:flex-[1_1_320px]">
              <SeatCard />
            </div>
          </div>
          <AccountCards />
        </Container>
      )}
    </>
  );
}

function AccountCards() {
  return (
    <div className="grid items-start gap-3.5 lg:grid-cols-2">
      <ChangePasswordCard />
      <SessionsCard />
    </div>
  );
}
